import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { 
  ArrowLeft, ChevronRight, Check, X, Flag, HelpCircle, 
  AlertTriangle, Plane, BookOpen, Award, Zap, Target, Trophy, 
  Star, Flame, Medal, Crosshair, Calendar, CalendarCheck, CheckCircle, Moon, Sunrise,
  Snowflake, Users, GraduationCap, Shield, TrendingUp, TrendingDown, Minus, Share2
} from "lucide-react";
import ExplainWithAI from "../components/ExplainWithAI";

// Icon mapping for badges (same as BadgesPage)
const badgeIconMap = {
  Plane: Plane,
  PlaneTakeoff: Plane,
  Target: Target,
  Award: Award,
  Trophy: Trophy,
  Star: Star,
  Flame: Flame,
  Calendar: Calendar,
  CalendarCheck: CalendarCheck,
  Medal: Medal,
  Crosshair: Crosshair,
  CheckCircle: CheckCircle,
  Moon: Moon,
  Sunrise: Sunrise,
};

// Mission icon mapping (same as MissionsPage)
const missionIconMap = {
  Zap: Zap,
  Crosshair: Crosshair,
  AlertTriangle: AlertTriangle,
  Target: Target,
  Trophy: Trophy,
  Award: Award,
  Star: Star,
  Moon: Moon,
  Sunrise: Sunrise,
  CheckCircle: CheckCircle,
  Snowflake: Snowflake,
  Users: Users,
  GraduationCap: GraduationCap,
};

// Requirement text mapping for badges
const badgeRequirementText = {
  "first_session": "Completou a sua primeira sessão de treino",
  "first_exam": "Completou o seu primeiro exame",
  "correct_50": "Acertou 50 respostas no total",
  "correct_100": "Acertou 100 respostas no total",
  "correct_500": "Acertou 500 respostas no total",
  "streak_3": "Treinou 3 dias consecutivos",
  "streak_7": "Treinou 7 dias consecutivos",
  "streak_30": "Treinou 30 dias consecutivos",
  "perfect_session": "Completou uma sessão com 100% de acerto",
  "exam_passed": "Passou num exame (75%+)",
  "night_owl": "Treinou depois da meia-noite",
  "early_bird": "Treinou antes das 6h da manhã",
  "first_login": "Completou o registo na plataforma",
};

const TrainingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [sections, setSections] = useState([]);
  const [cadernos, setCadernos] = useState([]);
  const [filterType, setFilterType] = useState("section"); // "section" or "caderno"
  const [selectedSection, setSelectedSection] = useState("all");
  const [selectedCaderno, setSelectedCaderno] = useState("all");
  const [numQuestions, setNumQuestions] = useState(10);
  const [evaluationMode, setEvaluationMode] = useState(false);
  // Live rules for the evaluation-mode badge text. Defaults match the new
  // server defaults (no cap, no cooldown) so the message is sensible even
  // before the fetch lands.
  const [evalRules, setEvalRules] = useState({
    min_questions: 15,
    daily_limit: 0,
    cooldown_minutes: 0,
  });
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [correctCount, setCorrectCount] = useState(0); // Track correct answers separately
  const [answeredCount, setAnsweredCount] = useState(0); // Track total answered
  const [loading, setLoading] = useState(false);
  const startingRef = useRef(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [finalResults, setFinalResults] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [showMissionPopup, setShowMissionPopup] = useState(false);
  const [completedMissions, setCompletedMissions] = useState([]);
  const [earnedBadges, setEarnedBadges] = useState([]);
  
  // Queue for showing achievements one by one
  const [achievementQueue, setAchievementQueue] = useState([]);
  const [currentAchievement, setCurrentAchievement] = useState(null);

  // Review state (end-of-session review, useful especially for evaluation mode)
  const [showReview, setShowReview] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  // Skill Rating delta returned by the backend at the end of an evaluation session
  const [skillRatingUpdate, setSkillRatingUpdate] = useState(null);

  const fetchReview = async () => {
    if (!session?.session_id || reviewData || reviewLoading) {
      setShowReview(true);
      return;
    }
    setReviewLoading(true);
    try {
      const resp = await axios.get(`${API}/training/${session.session_id}/review`, { withCredentials: true });
      setReviewData(resp.data);
      setShowReview(true);
    } catch (error) {
      console.error("Error fetching review:", error);
      toast.error("Erro ao carregar revisão");
    } finally {
      setReviewLoading(false);
    }
  };
  
  // Scroll state for gradient indicator
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const contentRef = useRef(null);
  
  // Check scroll position to show/hide gradient indicator
  useEffect(() => {
    const checkScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        setCanScrollUp(scrollTop > 20);
        setCanScrollDown(scrollTop < scrollHeight - clientHeight - 20);
      }
    };
    
    const contentEl = contentRef.current;
    if (contentEl) {
      contentEl.addEventListener('scroll', checkScroll);
      checkScroll();
      // Re-check after content loads
      setTimeout(checkScroll, 100);
      window.addEventListener('resize', checkScroll);
      
      return () => {
        contentEl.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [currentQuestion]);

  useEffect(() => {
    fetchFilters();
  }, []);

  // Handle preselected section from navigation state
  useEffect(() => {
    if (location.state?.preselectedSection) {
      setFilterType("section");
      setSelectedSection(location.state.preselectedSection);
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchFilters = async () => {
    try {
      const [sectionsRes, cadernosRes, rulesRes] = await Promise.all([
        axios.get(`${API}/sections`, { withCredentials: true }),
        axios.get(`${API}/cadernos`, { withCredentials: true }),
        axios.get(`${API}/training/evaluation-rules`, { withCredentials: true }),
      ]);
      setSections(sectionsRes.data);
      setCadernos(cadernosRes.data);
      if (rulesRes.data) setEvalRules(rulesRes.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const startSession = async () => {
    // A double click fires twice before the disabled state renders: ignore the second one.
    if (startingRef.current) return;
    startingRef.current = true;
    setLoading(true);
    setSkillRatingUpdate(null);
    try {
      const payload = {
        num_questions: numQuestions,
        section_filter: filterType === "section" && selectedSection !== "all" ? selectedSection : null,
        caderno_filter: filterType === "caderno" && selectedCaderno !== "all" ? selectedCaderno : null,
        evaluation_mode: evaluationMode
      };
      
      const response = await axios.post(`${API}/training/start`, payload, { withCredentials: true });
      
      setSession(response.data);
      setCurrentQuestion(response.data.question);
      setSelectedAnswer(null);
      setShowResult(false);
      setCorrectCount(0);
      setAnsweredCount(0);
    } catch (error) {
      console.error("Error starting session:", error);
      toast.error(error.response?.data?.detail || "Erro ao iniciar sessão");
    } finally {
      setLoading(false);
      startingRef.current = false;
    }
  };

  const submitAnswer = async () => {
    if (selectedAnswer === null || !currentQuestion) return;
    
    setLoading(true);
    try {
      // Get the answer_id from the selected answer object
      const selectedAnswerObj = currentQuestion.answers[selectedAnswer];
      const answerId = selectedAnswerObj?.id || selectedAnswerObj;
      
      const response = await axios.post(
        `${API}/training/${session.session_id}/answer`,
        {
          question_id: currentQuestion.question_id,
          answer_id: answerId  // NOW SENDS ANSWER ID INSTEAD OF INDEX
        },
        { withCredentials: true }
      );
      
      setLastResult(response.data);

      // In evaluation mode (no intermediate feedback), auto-advance to next
      // question instead of showing the answer highlighted.
      const hideFeedback = response.data.is_correct === null && !response.data.session_complete;
      const isEvalSession = response.data.evaluation_mode || session?.evaluation_mode;
      if (hideFeedback && response.data.next_question) {
        setCurrentQuestion(response.data.next_question);
        setSelectedAnswer(null);
        setShowResult(false);
        // IMPORTANT: keep the header progress in sync even in eval mode —
        // otherwise the "Questão X de N" counter and the segmented progress
        // bar stay frozen on the first item.
        setSession(prev => prev ? { ...prev, current_index: (prev.current_index || 0) + 1 } : prev);
      } else if (isEvalSession && response.data.session_complete) {
        // Evaluation mode + last question: skip the per-question result screen
        // and jump directly to the final results.
        setShowResult(false);
        finishSession({
          correct: response.data.correct_count ?? correctCount,
          total: response.data.total_answered ?? answeredCount,
          completedMissions: response.data.missions_completed?.completed_missions || [],
          earnedBadges: response.data.missions_completed?.earned_badges || [],
        });
      } else {
        setShowResult(true);
      }

      // Update counters from response (eval mode returns null mid-session → keep prior counters)
      if (response.data.correct_count !== null && response.data.correct_count !== undefined) {
        setCorrectCount(response.data.correct_count);
      }
      setAnsweredCount(response.data.total_answered || 0);
      
      // Store missions/badges but DON'T show popup yet - wait for session finish
      if (response.data.session_complete && response.data.missions_completed) {
        // Save for later - popup will show when user clicks "Ver Resultados"
        setCompletedMissions(response.data.missions_completed.completed_missions || []);
        setEarnedBadges(response.data.missions_completed.earned_badges || []);
      }

      // Capture skill rating delta (only set in evaluation-mode completed sessions)
      if (response.data.session_complete && response.data.skill_rating_update) {
        setSkillRatingUpdate(response.data.skill_rating_update);
      }
      
      if (response.data.session_complete) {
        // Don't auto-complete, let user see last answer first
      }
    } catch (error) {
      console.error("Error submitting answer:", error);
      toast.error("Erro ao submeter resposta");
    } finally {
      setLoading(false);
    }
  };

  const finishSession = (override = null) => {
    setSessionComplete(true);
    setFinalResults({
      correct: override?.correct ?? correctCount,
      total: override?.total ?? answeredCount,
    });

    // Build queue of achievements to show one by one (use override if provided —
    // necessary for the eval-mode auto-finish path, where state may be stale).
    const badges = override?.earnedBadges ?? earnedBadges;
    const missions = override?.completedMissions ?? completedMissions;
    const queue = [];
    badges.forEach((badge) => queue.push({ type: 'badge', data: badge }));
    missions.forEach((mission) => queue.push({ type: 'mission', data: mission }));

    if (queue.length > 0) {
      setAchievementQueue(queue);
      setTimeout(() => {
        setCurrentAchievement(queue[0]);
        setShowMissionPopup(true);
      }, 500);
    }
  };
  
  const showNextAchievement = () => {
    const currentIndex = achievementQueue.findIndex(a => 
      a.type === currentAchievement?.type && 
      (a.type === 'badge' ? a.data.badge_id === currentAchievement.data.badge_id : a.data.mission_id === currentAchievement.data.mission_id)
    );
    
    if (currentIndex < achievementQueue.length - 1) {
      // Show next achievement
      setCurrentAchievement(achievementQueue[currentIndex + 1]);
    } else {
      // No more achievements, close popup
      setShowMissionPopup(false);
      setCurrentAchievement(null);
      setAchievementQueue([]);
      setCompletedMissions([]);
      setEarnedBadges([]);
    }
  };

  const nextQuestion = () => {
    if (lastResult?.session_complete) {
      // Last question answered, now show results
      finishSession();
    } else if (lastResult?.next_question) {
      setCurrentQuestion(lastResult.next_question);
      setSelectedAnswer(null);
      setShowResult(false);
      setLastResult(null);
      setSession(prev => ({
        ...prev,
        current_index: prev.current_index + 1
      }));
    }
  };

  const reportQuestion = async () => {
    if (!reportReason.trim()) {
      toast.error("Por favor indique o motivo da denúncia");
      return;
    }
    try {
      await axios.post(
        `${API}/questions/${currentQuestion.question_id}/report`,
        { reason: reportReason },
        { withCredentials: true }
      );
      toast.success("Questão reportada com sucesso");
      setReportDialogOpen(false);
      setReportReason("");
    } catch (error) {
      toast.error("Erro ao reportar questão");
    }
  };

  // Setup Screen
  if (!session) {
    return (
      <div className="min-h-screen bg-cockpit-bg">
        <header className="border-b border-cockpit-border bg-cockpit-panel/50 backdrop-blur-sm">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-chivo text-lg text-cockpit-text-bright">Treino Adaptativo</h1>
                <p className="text-xs text-slate-500 font-mono">Configure a sua sessão</p>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-6 py-12">
          <div className="max-w-md mx-auto">
            <Card className="instrument-panel">
              <CardContent className="p-8 space-y-6">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Plane className="w-8 h-8 text-orange-500" />
                  </div>
                  <h2 className="font-chivo text-xl text-cockpit-text-bright">Nova Sessão de Treino</h2>
                </div>

                {/* Filter Type Selection */}
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-slate-500">Treinar por</label>
                  <div className="flex gap-2">
                    <Button
                      variant={filterType === "section" ? "default" : "outline"}
                      onClick={() => setFilterType("section")}
                      className={filterType === "section" ? "btn-cockpit flex-1" : "btn-secondary-cockpit flex-1"}
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Disciplina
                    </Button>
                    <Button
                      variant={filterType === "caderno" ? "default" : "outline"}
                      onClick={() => setFilterType("caderno")}
                      className={filterType === "caderno" ? "btn-cockpit flex-1" : "btn-secondary-cockpit flex-1"}
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Caderno
                    </Button>
                  </div>
                </div>

                {/* Section or Caderno Selection */}
                {filterType === "section" ? (
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-wider text-slate-500">Disciplina</label>
                    <Select value={selectedSection} onValueChange={setSelectedSection}>
                      <SelectTrigger className="input-cockpit" data-testid="section-select">
                        <SelectValue placeholder="Selecione uma disciplina" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="all">Todas as Disciplinas</SelectItem>
                        {[...sections].sort((a, b) => a.section_name.localeCompare(b.section_name, 'pt')).map((section) => (
                          <SelectItem 
                            key={section.section_id} 
                            value={section.section_id}
                            disabled={section.is_locked}
                            className={section.is_locked ? 'opacity-50' : ''}
                          >
                            {section.is_locked && '🔒 '}{section.section_name} ({section.count})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {sections.some(s => s.is_locked) && (
                      <p className="text-xs text-yellow-400 mt-1">
                        🔒 Disciplinas bloqueadas só podem ser desbloqueadas pelo instrutor.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-wider text-slate-500">Caderno</label>
                    <Select value={selectedCaderno} onValueChange={setSelectedCaderno}>
                      <SelectTrigger className="input-cockpit" data-testid="caderno-select">
                        <SelectValue placeholder="Selecione um caderno" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="all">Todos os Cadernos</SelectItem>
                        {[...cadernos].sort((a, b) => a.caderno_name.localeCompare(b.caderno_name, 'pt')).map((caderno) => (
                          <SelectItem key={caderno.caderno_id} value={caderno.caderno_id}>
                            {caderno.caderno_name} ({caderno.count} questões)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Caderno I: Comportamento e Limitações Humanas, Legislação Aérea, Meteorologia e Navegação e Planeamento<br/>
                      Caderno II: Conhecimento Geral de Aeronaves, Massa e Centragem, Princípios de Voo e Procedimentos Operacionais<br/>
                      Comunicações: Comunicações em Inglês/Português
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-slate-500">Número de Questões</label>
                  <Select value={numQuestions.toString()} onValueChange={(v) => setNumQuestions(parseInt(v))}>
                    <SelectTrigger className="input-cockpit" data-testid="num-questions-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="10">10 questões</SelectItem>
                      <SelectItem value="15">15 questões</SelectItem>
                      <SelectItem value="20">20 questões</SelectItem>
                      <SelectItem value="30">30 questões</SelectItem>
                      <SelectItem value="50">50 questões</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Evaluation mode toggle — affects Skill Rating */}
                <div className="border border-purple-500/30 rounded-lg p-3 bg-purple-500/5">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="evaluation-mode-label">
                    <input
                      type="checkbox"
                      checked={evaluationMode}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setEvaluationMode(on);
                        // If turning on with fewer than min_questions, bump up
                        if (on && numQuestions < evalRules.min_questions) {
                          setNumQuestions(evalRules.min_questions);
                        }
                      }}
                      className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-purple-500 focus:ring-purple-500 cursor-pointer"
                      data-testid="evaluation-mode-checkbox"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-purple-200">
                        Modo Avaliação — conta para o Skill Rating
                      </div>
                      <div className="text-xs text-slate-400 mt-1 leading-relaxed">
                        {(() => {
                          const parts = [
                            "Sem feedback entre perguntas",
                            `mínimo ${evalRules.min_questions} perguntas`,
                          ];
                          if (evalRules.daily_limit > 0) {
                            parts.push(`máx. ${evalRules.daily_limit} por dia`);
                          }
                          if (evalRules.cooldown_minutes > 0) {
                            parts.push(`cooldown ${evalRules.cooldown_minutes} min por disciplina`);
                          }
                          parts.push("perguntas 100% aleatórias");
                          return parts.join(" · ") + ".";
                        })()}
                      </div>
                      {evaluationMode && (
                        <div className="text-[11px] text-amber-300 mt-1.5 font-medium">
                          ⚠ O teu Skill Rating vai mudar consoante a tua performance.
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                <Button 
                  onClick={startSession}
                  disabled={loading}
                  data-testid="start-session-btn"
                  className="btn-cockpit w-full py-4"
                >
                  {loading ? "A iniciar..." : (evaluationMode ? "Iniciar Avaliação" : "Iniciar Treino")}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // Session Complete Screen
  if (sessionComplete) {
    const accuracy = ((finalResults.correct / finalResults.total) * 100).toFixed(1);
    const passed = accuracy >= 75;

    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center p-6">
        {/* Achievement Popup - shows missions/badges one at a time */}
        <Dialog open={showMissionPopup} onOpenChange={(open) => {
          if (!open) {
            showNextAchievement();
          }
        }}>
          <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-md [&>button]:hidden">
            <DialogHeader>
              <DialogTitle className="font-chivo text-center flex items-center justify-center gap-2">
                {currentAchievement?.type === 'badge' ? (
                  <>
                    <Award className="w-6 h-6 text-orange-400" />
                    Novo Badge Desbloqueado!
                  </>
                ) : (
                  <>
                    <Zap className="w-6 h-6 text-yellow-400" />
                    Missão Completa!
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="py-6 text-center">
              {/* BADGE Achievement */}
              {currentAchievement?.type === 'badge' && (
                <>
                  <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 bg-gradient-to-b from-orange-500/30 to-orange-600/20 border-2 border-orange-500/50">
                    {(() => {
                      const IconComponent = badgeIconMap[currentAchievement.data.icon] || Award;
                      return <IconComponent className="w-12 h-12 text-orange-400" />;
                    })()}
                  </div>
                  <h3 className="text-cockpit-text-bright font-chivo text-xl mb-2">
                    {currentAchievement.data.name || 'Badge'}
                  </h3>
                  {currentAchievement.data.description && (
                    <p className="text-sm text-slate-400 mb-4">{currentAchievement.data.description}</p>
                  )}
                  <div className="bg-slate-900/50 rounded-lg p-4 mt-2 mb-4">
                    <p className="text-xs text-slate-500 uppercase mb-1">Motivo da conquista</p>
                    <p className="text-sm text-slate-300">
                      {badgeRequirementText[currentAchievement.data.requirement] || currentAchievement.data.description || 'Parabéns pela conquista!'}
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                    <p className="text-xs text-emerald-400">✓ Conquistado agora!</p>
                  </div>
                </>
              )}
              
              {/* MISSION Achievement */}
              {currentAchievement?.type === 'mission' && (
                <>
                  <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 bg-emerald-500/20 border-2 border-emerald-500/50">
                    {(() => {
                      const IconComponent = missionIconMap[currentAchievement.data.icon] || CheckCircle;
                      return <IconComponent className="w-12 h-12 text-emerald-400" />;
                    })()}
                  </div>
                  <h3 className="text-cockpit-text-bright font-chivo text-xl mb-2">
                    {currentAchievement.data.title || currentAchievement.data.name || 'Missão'}
                  </h3>
                  {currentAchievement.data.description && (
                    <p className="text-sm text-slate-400 mb-4">{currentAchievement.data.description}</p>
                  )}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-400 font-mono text-xl font-bold">
                        +{currentAchievement.data.xp_reward || 0} XP
                      </span>
                    </div>
                  </div>
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                    <p className="text-xs text-emerald-400">✓ Missão completa!</p>
                  </div>
                </>
              )}
              
              {/* Queue Counter */}
              {achievementQueue.length > 1 && (
                <div className="mb-4 text-xs text-slate-500">
                  {achievementQueue.findIndex(a => 
                    a.type === currentAchievement?.type && 
                    (a.type === 'badge' ? a.data.badge_id === currentAchievement?.data.badge_id : a.data.mission_id === currentAchievement?.data.mission_id)
                  ) + 1} de {achievementQueue.length} conquistas
                </div>
              )}
              
              <Button 
                onClick={showNextAchievement}
                className="btn-cockpit w-full"
                data-testid="achievement-popup-continue-btn"
              >
                {achievementQueue.findIndex(a => 
                  a.type === currentAchievement?.type && 
                  (a.type === 'badge' ? a.data.badge_id === currentAchievement?.data.badge_id : a.data.mission_id === currentAchievement?.data.mission_id)
                ) < achievementQueue.length - 1 ? 'Próxima Conquista' : 'Continuar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Card className="instrument-panel max-w-3xl w-full">
          <CardContent className="p-8">
            {!showReview ? (
              <div className="text-center">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                  passed ? 'bg-emerald-500/20' : 'bg-red-500/20'
                }`}>
                  {passed ? (
                    <Check className="w-10 h-10 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-10 h-10 text-red-400" />
                  )}
                </div>

                <h2 className="font-chivo text-2xl text-cockpit-text-bright mb-2">
                  {passed ? "Excelente!" : "Continue a Treinar"}
                </h2>

                <p className="text-slate-400 mb-6">
                  {passed
                    ? "Ótimo desempenho nesta sessão!"
                    : "Reveja as matérias e tente novamente."}
                </p>

                {/* Skill Rating delta pill — only for evaluation-mode sessions */}
                {skillRatingUpdate && (
                  <SkillRatingDeltaPill update={skillRatingUpdate} />
                )}

                <div className="bg-slate-900/50 rounded-lg p-6 mb-6">
                  <div className="font-mono text-4xl text-orange-400 glow-orange mb-2">
                    {accuracy}%
                  </div>
                  <div className="text-sm text-slate-500">
                    {finalResults.correct} de {finalResults.total} corretas
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={fetchReview}
                    disabled={reviewLoading}
                    data-testid="review-answers-btn"
                    className="btn-cockpit w-full"
                  >
                    {reviewLoading ? "A carregar..." : "Rever Respostas"}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                  <ShareResultButton
                    accuracy={accuracy}
                    correct={finalResults.correct}
                    total={finalResults.total}
                    isEvaluation={!!skillRatingUpdate}
                    skillRatingUpdate={skillRatingUpdate}
                  />
                  <Button
                    onClick={() => {
                      setSession(null);
                      setSessionComplete(false);
                      setFinalResults(null);
                      setReviewData(null);
                      setShowReview(false);
                      setSkillRatingUpdate(null);
                    }}
                    data-testid="new-session-btn"
                    variant="outline"
                    className="btn-secondary-cockpit w-full"
                  >
                    Nova Sessão
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/dashboard")}
                    data-testid="back-dashboard-btn"
                    className="btn-secondary-cockpit w-full"
                  >
                    Voltar ao Painel
                  </Button>
                </div>
              </div>
            ) : (
              <SessionReview
                data={reviewData}
                accuracy={accuracy}
                onClose={() => setShowReview(false)}
                onNewSession={() => {
                  setSession(null);
                  setSessionComplete(false);
                  setFinalResults(null);
                  setReviewData(null);
                  setShowReview(false);
                  setSkillRatingUpdate(null);
                }}
                onBackToDashboard={() => navigate("/dashboard")}
              />
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Training Question Screen
  return (
    <div className="h-screen bg-cockpit-bg flex flex-col overflow-hidden">
      {/* Header with Progress */}
      <header className="border-b border-cockpit-border bg-cockpit-panel/50 backdrop-blur-sm shrink-0">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-chivo text-base sm:text-lg text-cockpit-text-bright">Treino</h1>
                <p className="text-xs text-slate-500 font-mono">
                  Questão {(session?.current_index || 0) + 1} de {session?.total_questions}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="font-mono text-sm">
                <span className="text-emerald-400">{correctCount}</span>
                <span className="text-slate-500"> / </span>
                <span className="text-slate-400">{answeredCount}</span>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setReportDialogOpen(true)}
                className="text-slate-400 hover:text-yellow-400"
                title="Reportar questão"
              >
                <Flag className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Report Dialog */}
          <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
            <DialogContent className="bg-cockpit-panel border-cockpit-border">
              <DialogHeader>
                <DialogTitle className="font-chivo">Reportar Questão</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-sm text-slate-400">
                  Indique o motivo da denúncia (erro no enunciado, resposta incorreta, etc.):
                </p>
                <Textarea
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  placeholder="Descreva o problema encontrado..."
                  className="input-cockpit min-h-[100px]"
                />
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setReportDialogOpen(false)}
                    className="btn-secondary-cockpit flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={reportQuestion}
                    className="btn-cockpit flex-1"
                  >
                    Enviar Denúncia
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Progress Bar */}
          <div className="mt-4 progress-segmented">
            {[...Array(session?.total_questions || 20)].map((_, i) => (
              <div 
                key={i} 
                className={`progress-segment ${
                  i < (session?.current_index || 0) ? 'active' : 
                  i === (session?.current_index || 0) ? 'bg-orange-500/50' : ''
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Question Content */}
      <main className="flex-1 relative overflow-hidden">
        {/* Top scroll gradient indicator */}
        <div className={`absolute top-0 left-0 right-0 h-16 z-10 pointer-events-none transition-opacity duration-300 ${
          canScrollUp ? 'opacity-100' : 'opacity-0'
        }`} style={{ background: 'linear-gradient(to bottom, rgb(15, 23, 42) 0%, rgb(15, 23, 42, 0.8) 40%, transparent 100%)' }} />
        
        {/* Bottom scroll gradient indicator - positioned above the fixed bar */}
        <div className={`absolute bottom-0 left-0 right-0 h-32 z-10 pointer-events-none transition-opacity duration-300 ${
          canScrollDown ? 'opacity-100' : 'opacity-0'
        }`} style={{ background: 'linear-gradient(to top, rgb(15, 23, 42) 0%, rgb(15, 23, 42, 0.9) 30%, rgb(15, 23, 42, 0.5) 60%, transparent 100%)' }} />
        
        {/* Scrollable content */}
        <div 
          ref={contentRef}
          className="h-full overflow-y-auto px-4 sm:px-6 py-6 pb-44 sm:pb-40"
        >  <div className="container mx-auto max-w-4xl">
            {currentQuestion && (
              <>
                {/* Section Badge */}
                <div className="badge-cockpit text-orange-400 inline-block mb-4">
                  {currentQuestion.section_name}
                </div>

                {/* Question Statement */}
                <Card className="instrument-panel mb-6">
                  <CardContent className="p-4 sm:p-6">
                    <p className="text-cockpit-text-bright text-base sm:text-lg leading-relaxed">
                      {currentQuestion.statement}
                    </p>
                    {currentQuestion.image && (
                      <img 
                        src={currentQuestion.image} 
                        alt="Question" 
                        className="mt-4 rounded-lg max-h-64 object-contain"
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Answer Options */}
                <div className="space-y-3">
                  {currentQuestion.answers.map((answer, index) => {
                    // answer is now an object with 'id' and 'text'
                    const answerId = answer?.id || answer;
                    const answerText = answer?.text || answer;
                    const isCorrectAnswer = lastResult?.correct_answer_id === answerId;
                    
                    return (
                      <AnswerOption
                        key={answerId}
                        index={index}
                        answer={answerText}
                        selected={selectedAnswer === index}
                        showResult={showResult}
                        isCorrect={isCorrectAnswer}
                        wasSelected={selectedAnswer === index}
                        onClick={() => !showResult && setSelectedAnswer(index)}
                      />
                    );
                  })}
                </div>

                {/* Explanation (only shown if available) */}
                {showResult && lastResult?.explanation && (
                  <div className="mt-4 p-4 rounded-lg border border-slate-700 bg-slate-900/50">
                    <p className="text-sm text-slate-400">{lastResult.explanation}</p>
                  </div>
                )}

                {/* AI Explanation — only when answer is wrong and in training (non-eval) mode */}
                {showResult && lastResult?.is_correct === false && !session?.evaluation_mode && (
                  <ExplainWithAI
                    key={currentQuestion.question_id}
                    question={{
                      question_id: currentQuestion.question_id,
                      statement: currentQuestion.statement,
                      answers: currentQuestion.answers,
                      correct_answer: lastResult.correct_answer_id,
                      note: lastResult.explanation || null,
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Fixed Action Button at Bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgb(15, 23, 42) 0%, transparent 100%)' }} />
        <div className="relative bg-slate-900/95 backdrop-blur-xl border-t border-slate-700/50 shadow-2xl shadow-black/50">
          <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-5 max-w-4xl">
            {!showResult ? (
              <Button 
                onClick={submitAnswer}
                disabled={selectedAnswer === null || loading}
                data-testid="submit-answer-btn"
                className="btn-cockpit w-full py-5 sm:py-6 text-base"
              >
                {loading
                  ? "A verificar..."
                  : session?.evaluation_mode
                    ? ((session.current_index || 0) + 1 >= (session.total_questions || 0)
                        ? "Ver Resultado Final"
                        : "Próxima Questão")
                    : "Confirmar Resposta"}
                {session?.evaluation_mode && !loading && <ChevronRight className="w-5 h-5 ml-2" />}
              </Button>
            ) : (
              <Button 
                onClick={nextQuestion}
                data-testid="next-question-btn"
                className="btn-cockpit w-full py-5 sm:py-6 text-base"
              >
                {lastResult?.session_complete ? "Ver Resultado Final" : "Próxima Questão"}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AnswerOption = ({ index, answer, selected, showResult, isCorrect, wasSelected, onClick }) => {
  let borderClass = "border-slate-700 hover:border-slate-600";
  let bgClass = "bg-slate-900/50";
  
  if (showResult) {
    if (isCorrect) {
      borderClass = "border-emerald-500";
      bgClass = "bg-emerald-500/10";
    } else if (wasSelected && !isCorrect) {
      borderClass = "border-red-500";
      bgClass = "bg-red-500/10";
    }
  } else if (selected) {
    borderClass = "border-orange-500";
    bgClass = "bg-orange-500/10";
  }

  const letters = ['A', 'B', 'C', 'D'];

  return (
    <button
      onClick={onClick}
      disabled={showResult}
      data-testid={`answer-option-${index}`}
      className={`w-full p-4 rounded-lg border ${borderClass} ${bgClass} transition-all text-left flex items-start gap-4 disabled:cursor-default`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono shrink-0 ${
        selected || (showResult && isCorrect) 
          ? 'bg-orange-500 text-white' 
          : 'bg-slate-800 text-slate-400'
      }`}>
        {letters[index]}
      </div>
      <span className="text-cockpit-text-bright">{answer}</span>
      {showResult && isCorrect && (
        <Check className="w-5 h-5 text-emerald-400 ml-auto shrink-0" />
      )}
      {showResult && wasSelected && !isCorrect && (
        <X className="w-5 h-5 text-red-400 ml-auto shrink-0" />
      )}
    </button>
  );
};

export default TrainingPage;

// =======================================================================
// Share Result Button — generates a Wordle-style shareable card via canvas
// and triggers the Web Share API (with download fallback for desktop).
// =======================================================================
const ShareResultButton = ({ accuracy, correct, total, isEvaluation, skillRatingUpdate }) => {
  const [busy, setBusy] = useState(false);

  const buildShareImage = async () => {
    const W = 1200;
    const H = 630;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Background — gradient matching app
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, "#0b1220");
    bg.addColorStop(1, "#0f1830");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Subtle grid pattern
    ctx.strokeStyle = "rgba(148,163,184,0.06)";
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += 60) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, H);
      ctx.stroke();
    }
    for (let y = 0; y < H; y += 60) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(W, y);
      ctx.stroke();
    }

    // Top brand bar
    ctx.fillStyle = "#f97316";
    ctx.fillRect(60, 60, 64, 64);
    // Plane silhouette inside the orange square
    ctx.fillStyle = "#fff";
    ctx.font = "bold 38px system-ui, -apple-system, sans-serif";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText("✈", 92, 92);

    ctx.fillStyle = "#e2e8f0";
    ctx.textAlign = "left";
    ctx.font = "bold 36px system-ui, -apple-system, sans-serif";
    ctx.fillText("ULM Training Centre", 144, 84);
    ctx.fillStyle = "#94a3b8";
    ctx.font = "20px system-ui, -apple-system, sans-serif";
    ctx.fillText("Treino para piloto de ultraleve", 144, 116);

    // Mode badge
    const modeText = isEvaluation ? "MODO AVALIAÇÃO" : "SESSÃO DE TREINO";
    const modeColor = isEvaluation ? "#a855f7" : "#22d3ee";
    ctx.font = "bold 18px system-ui, -apple-system, sans-serif";
    const tw = ctx.measureText(modeText).width;
    ctx.fillStyle = `${modeColor}20`;
    roundRect(ctx, 60, 170, tw + 32, 38, 19);
    ctx.fill();
    ctx.strokeStyle = `${modeColor}80`;
    ctx.lineWidth = 1.5;
    roundRect(ctx, 60, 170, tw + 32, 38, 19);
    ctx.stroke();
    ctx.fillStyle = modeColor;
    ctx.fillText(modeText, 76, 189);

    // Big accuracy
    ctx.fillStyle = "#fb923c";
    ctx.font = "bold 180px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(`${accuracy}%`, 60, 360);

    ctx.fillStyle = "#94a3b8";
    ctx.font = "26px system-ui, -apple-system, sans-serif";
    ctx.fillText(`${correct} de ${total} corretas`, 60, 440);

    // Skill rating delta block (right side) — only for evaluation mode
    if (skillRatingUpdate) {
      const delta = Number(skillRatingUpdate.delta || 0);
      const positive = delta > 0;
      const color = positive ? "#10b981" : delta < 0 ? "#ef4444" : "#94a3b8";
      const arrow = positive ? "↑" : delta < 0 ? "↓" : "—";
      const sign = positive ? "+" : "";
      const action = positive ? "SUBIU" : delta < 0 ? "DESCEU" : "SEM ALTERAÇÃO";

      // Card — moved up to give breathing room above the QR code
      const x = 700;
      const y = 170;
      const w = 440;
      const h = 200;
      ctx.fillStyle = `${color}15`;
      roundRect(ctx, x, y, w, h, 16);
      ctx.fill();
      ctx.strokeStyle = `${color}80`;
      ctx.lineWidth = 2;
      roundRect(ctx, x, y, w, h, 16);
      ctx.stroke();

      ctx.fillStyle = "#94a3b8";
      ctx.font = "bold 18px system-ui, -apple-system, sans-serif";
      ctx.fillText(`SKILL RATING · ${action}`, x + 24, y + 36);

      ctx.fillStyle = color;
      ctx.font = "bold 88px system-ui, -apple-system, sans-serif";
      ctx.fillText(`${sign}${delta} ${arrow}`, x + 24, y + 120);

      ctx.fillStyle = "#cbd5e1";
      ctx.font = "22px system-ui, -apple-system, sans-serif";
      ctx.fillText(
        `${skillRatingUpdate.old_rating}  →  ${skillRatingUpdate.new_rating}`,
        x + 24,
        y + 168,
      );
    }

    // Footer (left aligned, leaves room for QR on the right)
    ctx.fillStyle = "#64748b";
    ctx.font = "20px system-ui, -apple-system, sans-serif";
    ctx.fillText("Treina e ganha pontos em ULM Training Centre", 60, H - 60);

    // QR code (bottom-right) — points to the public school URL
    const appUrl = "https://utc.com.pt";
    try {
      const qrCanvas = document.createElement("canvas");
      await QRCode.toCanvas(qrCanvas, appUrl, {
        width: 140,
        margin: 1,
        errorCorrectionLevel: "M",
        color: { dark: "#0f1830", light: "#e2e8f0" },
      });
      const qrSize = 140;
      const qrX = W - qrSize - 60;
      const qrY = H - qrSize - 40;
      // Light card behind QR for contrast
      ctx.fillStyle = "#e2e8f0";
      roundRect(ctx, qrX - 8, qrY - 8, qrSize + 16, qrSize + 16, 12);
      ctx.fill();
      ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);
      // Scan-me label
      ctx.fillStyle = "#94a3b8";
      ctx.font = "bold 14px system-ui, -apple-system, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("Scan para treinar", qrX + qrSize / 2, qrY - 18);
      ctx.textAlign = "left";
    } catch (qrErr) {
      // QR is non-critical — image generation must still succeed
      console.warn("QR generation failed:", qrErr);
    }

    return await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
  };

  const handleShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await buildShareImage();
      if (!blob) {
        toast.error("Não foi possível gerar a imagem");
        return;
      }
      const file = new File([blob], "ulm-resultado.png", { type: "image/png" });
      const text = isEvaluation && skillRatingUpdate
        ? `Sessão de Avaliação ULM: ${accuracy}% (${correct}/${total}) · Skill Rating ${skillRatingUpdate.delta >= 0 ? "+" : ""}${skillRatingUpdate.delta}`
        : `Sessão de Treino ULM: ${accuracy}% (${correct}/${total} corretas)`;

      // Try Web Share API with files (mobile / modern browsers)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "ULM Training Centre",
          text,
        });
        return;
      }
      // Try plain text share (Android Chrome, etc.)
      if (navigator.share) {
        // Fallback: download the image AND open share with text
        triggerDownload(blob);
        await navigator.share({ title: "ULM Training Centre", text }).catch(() => {});
        return;
      }
      // Desktop fallback: download image and copy text
      triggerDownload(blob);
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Imagem descarregada e texto copiado!");
      } catch {
        toast.success("Imagem descarregada!");
      }
    } catch (err) {
      console.error("Share error:", err);
      if (err?.name !== "AbortError") {
        toast.error("Erro ao partilhar resultado");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      onClick={handleShare}
      disabled={busy}
      variant="outline"
      data-testid="share-result-btn"
      className="btn-secondary-cockpit w-full"
    >
      <Share2 className="w-4 h-4 mr-2" />
      {busy ? "A preparar..." : "Partilhar Resultado"}
    </Button>
  );
};

// Helper: rounded rectangle path
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// Helper: trigger blob download
function triggerDownload(blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "ulm-resultado.png";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// =======================================================================
// Skill Rating Delta Pill — animated entrance, shown only after evaluation
// sessions to give immediate feedback on Elo movement.
// =======================================================================
const SkillRatingDeltaPill = ({ update }) => {
  if (!update) return null;
  const delta = Number(update.delta || 0);
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  const Icon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;
  const color = isPositive
    ? "from-emerald-500/30 to-emerald-600/10 border-emerald-500/50 text-emerald-300"
    : isNegative
      ? "from-red-500/30 to-red-600/10 border-red-500/50 text-red-300"
      : "from-slate-500/20 to-slate-600/10 border-slate-500/50 text-slate-300";
  const label = isPositive ? "Subiu!" : isNegative ? "Desceu" : "Sem alteração";
  const sign = isPositive ? "+" : "";
  return (
    <div
      data-testid="skill-rating-delta-pill"
      className={`mb-6 mx-auto inline-flex items-center gap-3 px-4 py-2.5 rounded-full border bg-gradient-to-r ${color} animate-elo-pop`}
      style={{ animation: "elo-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both" }}
    >
      <Shield className="w-4 h-4 opacity-80" />
      <div className="flex items-baseline gap-2 text-left">
        <span className="text-[10px] uppercase tracking-wider opacity-70">Skill Rating · {label}</span>
        <span className="font-mono text-base font-bold">
          {sign}{delta}
        </span>
        <span className="text-[11px] opacity-70 font-mono">
          ({update.old_rating} → {update.new_rating})
        </span>
      </div>
      <Icon className="w-4 h-4" />
      <style>{`
        @keyframes elo-pop {
          0% { opacity: 0; transform: scale(0.6) translateY(10px); }
          60% { opacity: 1; transform: scale(1.08) translateY(-2px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

// =======================================================================
// Session Review — end-of-session per-question breakdown, especially useful
// in Evaluation Mode where intermediate feedback is suppressed.
// =======================================================================
const SessionReview = ({ data, accuracy, onClose, onNewSession, onBackToDashboard }) => {
  const [onlyWrong, setOnlyWrong] = useState(false);
  if (!data) return null;
  const items = data.items || [];
  const letters = ["A", "B", "C", "D", "E"];
  const wrongCount = items.filter((it) => !it.is_correct).length;
  const visibleItems = onlyWrong ? items.filter((it) => !it.is_correct) : items;

  return (
    <div data-testid="session-review">
      {/* Header summary */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 pb-4 border-b border-slate-800">
        <div>
          <h2 className="font-chivo text-xl text-cockpit-text-bright">Revisão da Sessão</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {data.evaluation_mode ? "Modo Avaliação" : "Treino normal"} · {items.length} questões
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="font-mono text-2xl text-orange-400">{accuracy}%</div>
            <div className="text-[11px] text-slate-500">{data.correct_count}/{items.length} corretas</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid="close-review-btn"
            className="btn-secondary-cockpit"
          >
            Fechar
          </Button>
        </div>
      </div>

      {/* Filter toggle */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <label
          className={`inline-flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-1.5 rounded-full border transition-colors ${
            onlyWrong
              ? "border-red-500/50 bg-red-500/10 text-red-300"
              : "border-slate-700 bg-slate-900/60 text-slate-400 hover:text-slate-200"
          }`}
          data-testid="only-wrong-toggle"
        >
          <input
            type="checkbox"
            checked={onlyWrong}
            onChange={(e) => setOnlyWrong(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500 cursor-pointer"
            data-testid="only-wrong-checkbox"
          />
          Só mostrar erradas ({wrongCount})
        </label>
        <span className="text-[11px] text-slate-500">
          {visibleItems.length} de {items.length} {visibleItems.length === 1 ? "questão" : "questões"}
        </span>
      </div>

      {/* Questions list */}
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {visibleItems.length === 0 ? (
          <div className="text-center py-8 text-emerald-400 text-sm" data-testid="no-wrong-empty">
            <Check className="w-8 h-8 mx-auto mb-2" />
            Sem erros nesta sessão. Excelente!
          </div>
        ) : (
          visibleItems.map((item) => {
          const borderClass = item.is_correct
            ? "border-emerald-500/40"
            : item.submitted_answer_id
              ? "border-red-500/40"
              : "border-slate-700";
          return (
            <div
              key={item.question_id}
              data-testid={`review-item-${item.index}`}
              className={`rounded-lg border ${borderClass} bg-slate-900/40 p-4`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="text-xs text-slate-500 font-mono">
                  Q{item.index + 1}
                  {item.section_name ? ` · ${item.section_name}` : ""}
                </div>
                {item.is_correct ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 font-medium">
                    <Check className="w-3 h-3" /> Correta
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] text-red-400 font-medium">
                    <X className="w-3 h-3" /> Errada
                  </span>
                )}
              </div>
              <p className="text-sm text-cockpit-text-bright mb-3 leading-relaxed">{item.statement}</p>
              {item.image && (
                <img src={item.image} alt="Questão" className="rounded-md max-h-56 object-contain mb-3" />
              )}
              <div className="space-y-1.5">
                {item.answers.map((a, idx) => {
                  const aid = a.id;
                  const isCorrect = aid === item.correct_answer_id;
                  const wasSubmitted = aid === item.submitted_answer_id;
                  let row = "border-slate-800 bg-slate-900/50 text-slate-300";
                  if (isCorrect) row = "border-emerald-500/50 bg-emerald-500/10 text-emerald-200";
                  else if (wasSubmitted && !isCorrect) row = "border-red-500/50 bg-red-500/10 text-red-200";
                  return (
                    <div
                      key={aid || idx}
                      className={`flex items-start gap-2 text-sm p-2 rounded border ${row}`}
                    >
                      <span className="w-5 h-5 shrink-0 flex items-center justify-center rounded bg-slate-800/80 text-[11px] font-mono text-slate-400">
                        {letters[idx] || idx + 1}
                      </span>
                      <span className="flex-1">{a.text}</span>
                      {isCorrect && <Check className="w-4 h-4 text-emerald-400 shrink-0" />}
                      {wasSubmitted && !isCorrect && <X className="w-4 h-4 text-red-400 shrink-0" />}
                    </div>
                  );
                })}
              </div>
              {item.explanation && (
                <div className="mt-3 p-3 rounded-md border border-slate-800 bg-slate-950/50">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Explicação</p>
                  <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{item.explanation}</p>
                </div>
              )}
              {/* Botão IA — apenas para respostas erradas */}
              {!item.is_correct && (
                <ExplainWithAI
                  key={item.question_id}
                  question={{
                    question_id: item.question_id,
                    statement: item.statement,
                    answers: item.answers,
                    correct_answer: item.correct_answer_id,
                    note: item.explanation || null,
                  }}
                />
              )}
            </div>
          );
        })
        )}
      </div>

      {/* Actions */}
      <div className="mt-5 pt-4 border-t border-slate-800 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button onClick={onNewSession} className="btn-cockpit w-full" data-testid="review-new-session-btn">
          Nova Sessão
        </Button>
        <Button
          variant="outline"
          onClick={onBackToDashboard}
          className="btn-secondary-cockpit w-full"
          data-testid="review-back-dashboard-btn"
        >
          Voltar ao Painel
        </Button>
      </div>
    </div>
  );
};
